// Theme (ACCT-014): light, dark or system. Light is the default for everyone (MG, 2026-10-03: the site follows
// Skool's bright look; dark is a choice, never the OS). Applied as data-theme (and the .dark class) on <html>,
// persisted in localStorage "mgs-theme" (the older "mgs_theme" key is still read).
import { create } from 'zustand'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'mgs-theme'
/** Used when nothing is stored (keep in sync with the inline script in index.html and users.theme on the API). */
export const DEFAULT_THEME: ThemePreference = 'light'
const LEGACY_KEY = 'mgs_theme'

function isPreference(v: unknown): v is ThemePreference {
  return v === 'light' || v === 'dark' || v === 'system'
}

export function getStoredTheme(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY)
    return isPreference(v) ? v : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

function media(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia('(prefers-color-scheme: dark)')
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return media()?.matches ? 'dark' : 'light'
  return pref
}

/** Browser chrome colours: the top bar surface (REDESIGN section 4; index.html ships one meta per OS scheme). */
const THEME_COLORS: Record<ResolvedTheme, string> = { light: '#FFFFFF', dark: '#1D1D1D' }

function apply(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.theme = resolved
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
  // Both metas follow the chosen theme, so an explicit choice wins over the OS scheme (F1 request 3).
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', THEME_COLORS[resolved]))
}

type ThemeState = { preference: ThemePreference; resolved: ResolvedTheme }

export const useThemeStore = create<ThemeState>(() => ({ preference: DEFAULT_THEME, resolved: 'light' }))

/** Sets, applies and persists the theme. */
export function setTheme(preference: ThemePreference, opts: { persist?: boolean } = {}): void {
  const resolved = resolveTheme(preference)
  apply(resolved)
  useThemeStore.setState({ preference, resolved })
  if (opts.persist === false) return
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
    localStorage.removeItem(LEGACY_KEY)
  } catch {
    /* private mode */
  }
}

let listening = false

/** Applies the stored theme and follows the OS setting while the preference is "system". */
export function initTheme(): void {
  setTheme(getStoredTheme(), { persist: false })
  const mq = media()
  if (!mq || listening) return
  listening = true
  const onChange = () => {
    if (useThemeStore.getState().preference === 'system') setTheme('system', { persist: false })
  }
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange)
  else mq.addListener(onChange)
}

/** Applies me.user.theme when the server copy differs (auth.ts calls this). */
export function syncThemeFromUser(theme: string | null | undefined): void {
  if (isPreference(theme) && theme !== useThemeStore.getState().preference) setTheme(theme)
}

export function useTheme() {
  const preference = useThemeStore((s) => s.preference)
  const resolved = useThemeStore((s) => s.resolved)
  return { preference, resolved, setTheme }
}
